import { useState } from "react";

// MUI Components
import { Box, Button, FormLabel, MenuItem, Select, SelectChangeEvent, Tooltip, Typography } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import { ClearFilterButton } from "components/Buttons";
import { FilterOptionsPill } from "components/FilterOptionsPill";

// Hooks
import { useIntl } from "react-intl";

// Icons
import icons from "enums/icons";
import InfoIcon from "@mui/icons-material/Info";

// Styles
import ChartFiltersStyles from "./ChartFilters.styles";

import { functionsList, hierarchiesList, teamsList } from "constants/constants";

const INITIAL_FILTERS_VALUES = {
    functions_filter: "All (default)",
    hierarchies_filter: "All (default)",
    teams_filter: "All (default)",
};

const ChartFilters = ({ id }: { id: string }) => {
    const intl = useIntl();
    const classes = ChartFiltersStyles();

    const [filters, setFilters] = useState(INITIAL_FILTERS_VALUES);

    const handleFiltersChange = (event: SelectChangeEvent<string>) => {
        const filterName = event.target.name;
        const newValue = event.target.value;
        const newFilters = { ...filters, [filterName]: newValue };
        setFilters(newFilters);
    };

    const handleClearFilters = (filterName?: "functions_filter" | "hierarchies_filter" | "teams_filter" | "all") => {
        if (filterName === "all") {
            setFilters(INITIAL_FILTERS_VALUES);
            return;
        }
        if (filterName) {
            const newFilters = { ...filters, [filterName]: "All (default)" };
            setFilters(newFilters);
        }
    };

    return (
        <Box className={classes.mainWrapper}>
            <Box display="flex">
                <Box className={classes.filtersWrapper}>
                    {/* Functions Filter */}
                    <Box sx={{ maxWidth: 200, mr: 1 }}>
                        <FormLabel className={classes.label}>
                            {intl.formatMessage({ id: "filters.labels.function" })}
                        </FormLabel>
                        <Select
                            id="functions_filter"
                            name="functions_filter"
                            className={classes.selectField}
                            value={filters.functions_filter}
                            onChange={handleFiltersChange}
                            IconComponent={(props) => (
                                <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                            )}
                            MenuProps={{
                                PaperProps: {
                                    className: classes.selectFilterPaper,
                                },
                            }}
                            renderValue={(selected) => {
                                if (selected === "All (default)") {
                                    return "All (default)";
                                }

                                const selectedItem = functionsList.find((fn) => fn.name === selected);
                                return selectedItem ? (
                                    <FilterOptionsPill
                                        filterId="function"
                                        type={selectedItem.label}
                                        sx={{ maxWidth: 160 }}
                                        label={
                                            <Box className={classes.pillContainer}>
                                                <Typography noWrap fontSize={12}>
                                                    {selectedItem.name}
                                                </Typography>
                                                <Box className={classes.pillCounter}>3</Box>
                                            </Box>
                                        }
                                    />
                                ) : null;
                            }}
                        >
                            {functionsList
                                .filter((fn) => fn.label !== "ALL")
                                .map((fn) => {
                                    return (
                                        <MenuItem key={fn.id} value={fn.name} className={classes.selectMenuItem}>
                                            <Box display="flex" alignItems="center">
                                                <FilterOptionsPill
                                                    filterId="function"
                                                    type={fn.label}
                                                    label={
                                                        <Box className={classes.pillContainer}>
                                                            <Typography noWrap fontSize={12}>
                                                                {fn.name}
                                                            </Typography>
                                                            <Box className={classes.pillCounter}>3</Box>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            <li className={classes.selectMenuClear}>
                                <ClearFilterButton
                                    isDisabled={filters.functions_filter === "All (default)"}
                                    onClick={() => handleClearFilters("functions_filter")}
                                />
                            </li>
                        </Select>
                    </Box>

                    {/* Hierarchy Filter */}
                    <Box sx={{ maxWidth: 200, mr: 1 }}>
                        <FormLabel className={classes.label}>
                            {intl.formatMessage({ id: "filters.labels.hierarchy" })}
                        </FormLabel>
                        <Select
                            id="hierarchies_filter"
                            name="hierarchies_filter"
                            className={classes.selectField}
                            value={filters.hierarchies_filter}
                            onChange={handleFiltersChange}
                            IconComponent={(props) => (
                                <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                            )}
                            MenuProps={{
                                PaperProps: {
                                    className: classes.selectFilterPaper,
                                },
                            }}
                            renderValue={(selected) => {
                                if (selected === "All (default)") {
                                    return "All (default)";
                                }

                                const selectedItem = hierarchiesList.find((hierarchy) => hierarchy.name === selected);
                                return selectedItem ? (
                                    <FilterOptionsPill filterId="hierarchy" label={selectedItem.name} />
                                ) : null;
                            }}
                        >
                            {hierarchiesList
                                .filter((hierarchy) => hierarchy.label !== "ALL")
                                .map((hierarchy) => {
                                    return (
                                        <MenuItem
                                            key={hierarchy.id}
                                            value={hierarchy.name}
                                            className={classes.selectMenuItem}
                                        >
                                            <Box display="flex" alignItems="center">
                                                <FilterOptionsPill
                                                    filterId="hierarchy"
                                                    label={
                                                        <Box className={classes.pillContainer}>
                                                            <Typography noWrap fontSize={12}>
                                                                {hierarchy.name}
                                                            </Typography>
                                                            <Box className={classes.pillCounter}>3</Box>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            <li className={classes.selectMenuClear}>
                                <ClearFilterButton
                                    isDisabled={filters.hierarchies_filter === "All (default)"}
                                    onClick={() => handleClearFilters("hierarchies_filter")}
                                />
                            </li>
                        </Select>
                    </Box>

                    {/* Team Filter */}
                    <Box sx={{ maxWidth: 200, mr: 1 }}>
                        <FormLabel className={classes.label}>
                            {intl.formatMessage({ id: "filters.labels.team" })}
                        </FormLabel>
                        <Select
                            id="teams_filter"
                            name="teams_filter"
                            className={classes.selectField}
                            value={filters.teams_filter}
                            onChange={handleFiltersChange}
                            IconComponent={(props) => (
                                <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                            )}
                            MenuProps={{
                                PaperProps: {
                                    className: classes.selectFilterPaper,
                                },
                            }}
                            renderValue={(selected) => {
                                if (selected === "All (default)") {
                                    return "All (default)";
                                }

                                const selectedItem = teamsList.find((team) => team.label === selected);
                                return selectedItem ? (
                                    <FilterOptionsPill filterId="team" label={selectedItem.label} />
                                ) : null;
                            }}
                        >
                            {teamsList
                                .filter((team) => team.label !== "All (default")
                                .map((team) => {
                                    return (
                                        <MenuItem key={team.id} value={team.label} className={classes.selectMenuItem}>
                                            <Box display="flex" alignItems="center">
                                                <FilterOptionsPill
                                                    filterId="team"
                                                    label={
                                                        <Box className={classes.pillContainer}>
                                                            <Typography noWrap fontSize={12}>
                                                                {team.label}
                                                            </Typography>
                                                            <Box className={classes.pillCounter}>3</Box>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            <li className={classes.selectMenuClear}>
                                <ClearFilterButton
                                    isDisabled={filters.teams_filter === "All (default)"}
                                    onClick={() => handleClearFilters("teams_filter")}
                                />
                            </li>
                        </Select>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<CustomIcon icon={icons.save} />}
                        onClick={() => console.log("Save clicked")}
                    >
                        Save Filters
                    </Button>
                </Box>

                <Box sx={{ maxWidth: 200 }} className={classes.savedFiltersWrapper}>
                    <Box display="flex" alignItems="center">
                        <Tooltip
                            arrow
                            placement="bottom-start"
                            sx={{ color: "info.main", fontSize: 20 }}
                            title={
                                <Typography>
                                    {intl.formatMessage({ id: "filters.loadSavedFilters.tooltip" })}
                                </Typography>
                            }
                        >
                            <InfoIcon className={classes.infoIcon} />
                        </Tooltip>
                        <FormLabel className={classes.label}>
                            {intl.formatMessage({ id: "filters.labels.loadSaved" })}
                        </FormLabel>
                    </Box>

                    <Select
                        id="load-saved-filters"
                        name="load-saved-filters"
                        className={classes.selectField}
                        IconComponent={(props) => (
                            <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                        )}
                        MenuProps={{
                            PaperProps: {
                                className: classes.selectFilterPaper,
                            },
                        }}
                    ></Select>
                </Box>
            </Box>
            <Box display="flex" justifyContent="end">
                <ClearFilterButton isDisabled onClick={() => handleClearFilters("all")} />
            </Box>
        </Box>
    );
};

export default ChartFilters;
